import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as fs from 'file-saver';
import * as ExcelJS from 'exceljs';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import * as moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ExcelGeneratorData, ExtraRowsCopyOptions } from '../../dto/excel-generation-data.dto';
import { FilesHelperService } from '../files-helper.service';

@Injectable({
  providedIn: 'root'
})
export class XlsxTemplateFillerServiceService {
  private xlsxFileMIMEType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  constructor(
    private http: HttpClient,
    private filesHelperService: FilesHelperService
  ) {}

  async generateAndOpenNewExcelFile(
    filePath: string,
    dataForTemplate: ExcelGeneratorData,
    fileName: string
  ): Promise<void> {
    try {
      const blob: Blob | undefined = await this.fetchExcelBlob(filePath);
      if (blob) {
        const generatedExcelBuffer = await this.generateExcelFile(
          blob,
          dataForTemplate
        );
        await this.saveGeneratedFile(generatedExcelBuffer, fileName);
      }
    } catch (error) {
      throw new Error(`Ошибка генерации Excel документа: ${error}`);
    }
  }

  async generateDocument(
    templatePath: string,
    templateData: ExcelGeneratorData
  ): Promise<Blob> {
    try {
      const templateBlob: Blob | undefined =
        await this.fetchExcelBlob(templatePath);
      const generatedExcelBuffer = await this.generateExcelFile(
        templateBlob,
        templateData
      );
      return new Blob([generatedExcelBuffer], {
        type: this.xlsxFileMIMEType
      });
    } catch (error) {
      throw new Error(`Ошибка генерации Excel документа: ${error}`);
      // console.error('Error handling Excel file:', error);
    }
  }

  private async fetchExcelBlob(filePath: string): Promise<Blob> {
    return firstValueFrom(this.http.get(filePath, { responseType: 'blob' }));
  }

  private async generateExcelFile(
    templateFileBlob: Blob,
    dataForTemplate: ExcelGeneratorData
  ): Promise<ExcelJS.Buffer> {
    const workbook = new ExcelJS.Workbook();
    const arrayBuffer = await this.blobToArrayBuffer(templateFileBlob);
    await workbook.xlsx.load(new Uint8Array(arrayBuffer));
    const filledWorkbook = this.fillTemplate(workbook, dataForTemplate);
    return filledWorkbook.xlsx.writeBuffer();
  }

  private fillTemplate(
    workbook: ExcelJS.Workbook,
    dataForTemplate: ExcelGeneratorData
  ) {
    const rowsCopyOptions = dataForTemplate.additionalOptions?.copyRows || [];
    this.copyRows(workbook, rowsCopyOptions);

    dataForTemplate.cellDataArray.forEach(cellData => {
      const editWorksheet = workbook.getWorksheet(cellData.pageNumber);
      if (editWorksheet) {
        const editedCell = editWorksheet.getCell(cellData.address);

        editedCell.value = cellData.value;
      }
    });

    if (dataForTemplate.additionalOptions?.removeCellPoints) {
      dataForTemplate.additionalOptions.removeCellPoints.forEach(rmPoints => {
        const editWorksheet = workbook.getWorksheet(rmPoints.pageNumber);
        if (editWorksheet) {
          this.deleteRows(editWorksheet, rmPoints.startRow, rmPoints.endRow);
        }
      });
    }

    return workbook;
  }

  private async blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        if (event.target?.result instanceof ArrayBuffer) {
          resolve(event.target.result);
        } else {
          reject(new Error('Error converting blob to array buffer.'));
        }
      };
      reader.onerror = () => {
        reject(new Error('Error converting blob to array buffer.'));
      };
      reader.readAsArrayBuffer(blob);
    });
  }

  private async saveGeneratedFile(
    generatedExcelBuffer: ExcelJS.Buffer,
    fileName: string
  ) {
    const generatedBlob = new Blob([generatedExcelBuffer], {
      type: this.xlsxFileMIMEType
    });

    const fullFileName = `${fileName} ${moment().format('DD.MM.YY HH-mm-ss')}.xlsx`;

    if (Capacitor.getPlatform() === 'web') {
      fs.saveAs(generatedBlob, fullFileName);
    } else {
      const base64String = (generatedExcelBuffer as Buffer).toString('base64');
      const config = {
        directory: Directory.Documents,
        path: fullFileName,
        recursive: true
      };

      await Filesystem.writeFile({
        ...config,
        data: base64String
      });

      await this.filesHelperService.openFile(
        config.path,
        this.xlsxFileMIMEType
      );
    }
  }

  // обходное решение для удаления строк документа, когда их количество велико
  // (так как штатный способ - не работает)
  // https://github.com/exceljs/exceljs/issues/674
  private deleteRows(
    ws: ExcelJS.Worksheet,
    startRow: number,
    endRow: number
  ): void {
    if (startRow > endRow) {
      return;
    }
    let numRowsForDeleteLeft: number = endRow - startRow + 1;
    const numRowsExpected: number = ws.rowCount - numRowsForDeleteLeft;
    while (numRowsForDeleteLeft > 1) {
      numRowsForDeleteLeft = Math.trunc(numRowsForDeleteLeft / 2) || 1;
      ws.spliceRows(startRow, numRowsForDeleteLeft);
    }
    if (ws.rowCount > numRowsExpected) {
      ws.spliceRows(startRow, ws.rowCount - numRowsExpected);
    }
  }

  private copyRows(workbook: ExcelJS.Workbook, options: ExtraRowsCopyOptions[]) {
    options.forEach(option => {
      const ws = workbook.getWorksheet(option.pageNumber);
      if (ws) {
        const affectedMerges: string[] = this.unMergeCellsAfter(ws, option.copyFromRow);
        ws.duplicateRow(option.copyFromRow, option.numRows, true);
        this.reMergeCellsAfter(ws, affectedMerges, option.copyFromRow, option.numRows);
        this.cloneRowsFormatting(ws, option.copyFromRow, option.numRows);
      }
    });
  }

  private unMergeCellsAfter(sheet: ExcelJS.Worksheet, fromRowIndex: number): string[] {
    const affectedMerges: string[] = [];
    sheet.model.merges?.forEach(merge => {
      const [, bottomRight] = merge.split(':');
      const bottomCell = sheet.getCell(bottomRight);
      if (Number(bottomCell.row) > fromRowIndex) {
        affectedMerges.push(merge);
        sheet.unMergeCells(bottomCell.address);
      }
    });
    return affectedMerges;
  }

  private reMergeCellsAfter(
    sheet: ExcelJS.Worksheet,
    originalMergedRanges: string[],
    fromRowIndex: number,
    rowsCount: number
  ): void {
    originalMergedRanges.forEach(merge => {
      const [topLeft, bottomRight] = merge.split(':');
      const topCell = sheet.getCell(topLeft);
      const bottomCell = sheet.getCell(bottomRight);
      const topRowIndex = Number(topCell.row);
      const bottomRowIndex = Number(bottomCell.row);
      const newTopRowIndex = topRowIndex > fromRowIndex
        ? topRowIndex + rowsCount
        : topRowIndex;
      const newBottomRowIndex = bottomRowIndex + rowsCount;
      sheet.mergeCells(newTopRowIndex, Number(topCell.col),
        newBottomRowIndex, Number(bottomCell.col));
    });
  }

  private cloneRowsFormatting(
    sheet: ExcelJS.Worksheet,
    sourceRowNumber: number,
    numRows: number
  ): void {
    const sourceRow = sheet.getRow(sourceRowNumber);
    for (let i = 0; i < numRows; ++i) {
      const newRowNumber = sourceRowNumber + i + 1;
      const targetRow = sheet.getRow(newRowNumber);
      sourceRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const targetCell = targetRow.getCell(colNumber);
        targetCell.style = { ...cell.style };
      });
      const merges = sheet.model.merges || [];
      for (const merge of merges) {
        const mergeRange = merge.split(':');
        const startCell = sheet.getCell(mergeRange[0]);
        if (String(startCell.row) === String(sourceRowNumber)) {
          const endCell = sheet.getCell(mergeRange[1]);
          const startCol = startCell.col;
          const endCol = endCell.col;
          sheet.mergeCells(newRowNumber, Number(startCol), newRowNumber, Number(endCol));
        }
      }
    }
  }
}
